/**
 * React Router v5 to TanStack Router Adapter
 *
 * This module provides utilities to bridge TanStack Router with legacy components
 * that expect React Router v5 props (location, history).
 *
 * This is a temporary solution during the migration. Once all components are updated
 * to use TanStack Router hooks directly, these adapters can be removed.
 */

import {
  useRouter,
  useParams,
  useSearch,
  Link as TanstackLink,
  LinkProps as TanstackLinkProps,
} from '@tanstack/react-router';
import type { ComponentType } from 'react';
import { forwardRef, useMemo } from 'react';

/**
 * React Router v5-compatible location shape
 */
export interface LegacyLocation {
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
}

/**
 * React Router v5-compatible history shape
 */
export interface LegacyHistory {
  push: (path: string) => void;
  replace: (path: string) => void;
  goBack: () => void;
  goForward: () => void;
}

/**
 * React Router v5-compatible match shape
 */
export interface LegacyMatch {
  params: Record<string, string | undefined>;
  isExact: boolean;
  path: string;
  url: string;
}

/**
 * Props that legacy components expect from React Router v5
 */
export interface LegacyRouterProps {
  location: LegacyLocation;
  history: LegacyHistory;
  match: LegacyMatch;
  searchParams: Record<string, unknown>;
}

/**
 * Hook to get React Router v5-compatible location, history, and match objects
 * from TanStack Router, plus the parsed searchParams object
 *
 * @returns Object containing location, history, match, and searchParams
 * @example
 * function MyAdapter() {
 *   const { location, history, match, searchParams } = useLegacyRouterProps();
 *   return <MyLegacyComponent
 *     location={location}
 *     history={history}
 *     match={match}
 *     searchParams={searchParams}
 *   />;
 * }
 */
export function useLegacyRouterProps(): LegacyRouterProps {
  const router = useRouter();
  const params = useParams({ strict: false });
  // Use useSearch hook to get the actual search params from TanStack Router
  const searchParams = useSearch({ strict: false });

  // Memoize searchParams to prevent unnecessary re-renders when object reference changes
  // but values are the same (compare by JSON stringification for deep equality)
  const memoizedSearchParams = useMemo(() => searchParams, [JSON.stringify(searchParams)]);

  // Convert search object to query string format for React Router v5 compatibility
  // TanStack Router uses JSON-first approach, so we need to serialize back to query string
  const searchString = useMemo(() => {
    if (memoizedSearchParams && Object.keys(memoizedSearchParams).length > 0) {
      const urlSearchParams = new URLSearchParams();
      Object.entries(memoizedSearchParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          urlSearchParams.append(key, String(value));
        }
      });
      return '?' + urlSearchParams.toString();
    }
    return '';
  }, [memoizedSearchParams]);

  // Memoize location object to prevent unnecessary re-renders
  const location: LegacyLocation = useMemo(
    () => ({
      pathname: router.state.location.pathname,
      search: searchString,
      hash: router.state.location.hash,
      state: router.state.location.state,
    }),
    [
      router.state.location.pathname,
      searchString,
      router.state.location.hash,
      router.state.location.state,
    ]
  );

  // Memoize history object since functions are stable
  const history: LegacyHistory = useMemo(
    () => ({
      push: (path: string) => {
        // Parse query string from path if present
        const [pathname, search] = path.split('?');
        const searchParams = search ? Object.fromEntries(new URLSearchParams(search)) : undefined;
        router.navigate({ to: pathname, search: searchParams });
      },
      replace: (path: string) => {
        // Parse query string from path if present
        const [pathname, search] = path.split('?');
        const searchParams = search ? Object.fromEntries(new URLSearchParams(search)) : undefined;
        router.navigate({ to: pathname, search: searchParams, replace: true });
      },
      goBack: () => router.history.back(),
      goForward: () => router.history.forward(),
    }),
    [router]
  );

  // Memoize match object
  const match: LegacyMatch = useMemo(
    () => ({
      params: params as Record<string, string | undefined>,
      isExact: true, // TanStack Router doesn't have exact matching concept
      path: router.state.location.pathname,
      url: router.state.location.pathname,
    }),
    [params, router.state.location.pathname]
  );

  return { location, history, match, searchParams: memoizedSearchParams };
}

/**
 * Higher-order component that wraps a legacy component with TanStack Router adapter
 *
 * @param Component - The legacy component that expects React Router v5 props
 * @returns Adapted component that works with TanStack Router
 *
 * @example
 * const MyAdaptedComponent = withRouterAdapter(MyLegacyComponent);
 *
 * // In route file:
 * export const Route = createLazyFileRoute('/my-route')({
 *   component: MyAdaptedComponent,
 * });
 */
export function withRouterAdapter<P = Record<string, never>>(
  Component: ComponentType<any>
): () => JSX.Element {
  return function RouterAdapter() {
    const { location, history, match, searchParams } = useLegacyRouterProps();
    return (
      <Component location={location} history={history} match={match} searchParams={searchParams} />
    );
  };
}

/**
 * React Router v5-compatible Link component
 *
 * Drop-in replacement for react-router-dom's Link that works with TanStack Router.
 * Accepts the same props as React Router v5's Link.
 *
 * @example
 * // Before (React Router v5):
 * import { Link } from 'react-router-dom';
 * <Link to="/pc">PC Search</Link>
 *
 * // After (TanStack Router):
 * import { Link } from 'lib/tanstack-router/routerAdapter';
 * <Link to="/pc">PC Search</Link>
 */
export const Link = forwardRef<HTMLAnchorElement, { to: string; [key: string]: any }>(function Link(
  { to, children, ...props },
  ref
) {
  return (
    <TanstackLink to={to} {...props} ref={ref}>
      {children}
    </TanstackLink>
  );
});
